import ApiClient from "./apiClient";

interface SriProfileResponse {
  success: boolean;
  data: {
    company: {
      ruc: string;
      razon_social: string;
      nombre_comercial: string;
      direccion_matriz: string;
      telefono: string;
      email_facturacion: string;
      establecimiento: string;
      punto_emision: string;
      obligado_contabilidad: string;
    };
  };
  message?: string;
}

interface SriUpdateProfileRequest {
  ruc: string;
  razon_social: string;
  nombre_comercial: string;
  direccion_matriz: string;
  telefono: string;
  email_facturacion: string;
  establecimiento: string;
  punto_emision: string;
  obligado_contabilidad: string;
}

interface SriCertificateUploadResponse {
  success: boolean;
  data?: {
    id: number;
    alias: string;
    subject: string;
    issuer: string;
    validFrom: string;
    validTo: string;
    isActive: boolean;
  };
  message?: string;
}

interface SriActiveCertificateResponse {
  success: boolean;
  data?: {
    certificate: {
      id: number;
      alias?: string;
      subject: string;
      issuer: string;
      valid_from: string;
      valid_to: string;
      is_active: boolean;
      status: string;
    };
  };
  message?: string;
}


interface CertificateListResponse {
  success: boolean;
  data: {
    certificates: any[]; // Usando any[] porque la estructura del backend es diferente
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  message?: string;
}

interface ActivateCertificateResponse {
  success: boolean;
  data?: {
    certificateId: number;
    alias: string;
    isActive: boolean;
    previousActiveId?: number;
  };
  message?: string;
}

interface CertificateLimitsResponse {
  success: boolean;
  data: {
    limits: {
      maxAllowed: number;
      currentCount: number;
      remainingSlots: number;
      limitReached: boolean;
    };
    breakdown: {
      total: number;
      active: number;
      expired: number;
      inactive: number;
    };
    suggestion: string;
  };
  message?: string;
}

export class SriApiClient {
  private static instance: SriApiClient;
  private readonly basePath = '/admin/sri';

  private constructor() {}

  public static getInstance(): SriApiClient {
    if (!SriApiClient.instance) {
      SriApiClient.instance = new SriApiClient();
    }
    return SriApiClient.instance;
  }

  /**
   * Obtiene el perfil de la empresa desde la API SRI
   */
  public async getProfile(): Promise<SriProfileResponse> {
    return ApiClient.get<SriProfileResponse>(`${this.basePath}/profile`);
  }

  /**
   * Actualiza el perfil de la empresa en la API SRI
   */
  public async updateProfile(profileData: SriUpdateProfileRequest): Promise<SriProfileResponse> {
    return ApiClient.put<SriProfileResponse>(`${this.basePath}/profile`, profileData);
  }

  /**
   * Sube un certificado digital a la API SRI
   */
  public async uploadCertificate(certificateFile: File, password: string, alias?: string): Promise<SriCertificateUploadResponse> {
    const formData = new FormData();
    formData.append('certificate', certificateFile);
    formData.append('password', password);
    if (alias) {
      formData.append('alias', alias);
    }

    return ApiClient.uploadFile<SriCertificateUploadResponse>(
      `${this.basePath}/certificates/upload`,
      formData
    );
  }

  /**
   * Elimina un certificado digital de la API SRI
   */
  public async deleteCertificate(certificateId: number): Promise<{ success: boolean; message?: string }> {
    return ApiClient.delete<{ success: boolean; message?: string }>(
      `${this.basePath}/certificates/${certificateId}`
    );
  }

  /**
   * Obtiene el certificado activo de la API SRI
   */
  public async getActiveCertificate(): Promise<SriActiveCertificateResponse> {
    return ApiClient.get<SriActiveCertificateResponse>(`${this.basePath}/certificates/active`);
  }

  /**
   * Prueba la conectividad con la API SRI
   */
  public async testConnection(): Promise<{ success: boolean; message: string }> {
    return ApiClient.get<{ success: boolean; message: string }>(`${this.basePath}/test-connection`);
  }

  /**
   * Lista todos los certificados del usuario
   */
  public async listCertificates(page = 1, limit = 50): Promise<CertificateListResponse> {
    return ApiClient.get<CertificateListResponse>(
      `${this.basePath}/certificates`,
      { page, limit }
    );
  }

  /**
   * Activa un certificado específico
   */
  public async activateCertificate(certificateId: number): Promise<ActivateCertificateResponse> {
    return ApiClient.put<ActivateCertificateResponse>(
      `${this.basePath}/certificates/${certificateId}/activate`,
      {}
    );
  }

  /**
   * Obtiene los límites de certificados del usuario
   */
  public async getCertificateLimits(): Promise<CertificateLimitsResponse> {
    return ApiClient.get<CertificateLimitsResponse>(`${this.basePath}/certificates/limits`);
  }

  public clearAuth(): void {}
}

// Exportar instancia singleton
export default SriApiClient.getInstance();
